import { t } from '../i18n/index.ts';
import { money } from '../lib/format.ts';
import { priceNow } from '../lib/menu.ts';
import type { Catalog, CatalogProduct } from '../lib/types.ts';
import { Dialog, EmptyState } from './ui.tsx';

/** A product sold in versions (sizes, colours): it is never sold itself, a variant is chosen. */
export const isMaster = (p: Pick<CatalogProduct, 'variant_attributes'> | undefined) => !!p?.variant_attributes?.length;

type Versioned = Pick<CatalogProduct, 'id' | 'name' | 'variant_of' | 'variant_values' | 'variant_attributes'> & { on_menu?: boolean };
/** The variants on sale (or all of them), in the order of the attributes' values (S, M, XL; then Red, Navy). */
export const sellableVariants = <T extends Versioned>(catalog: { products: readonly T[] }, master: string, all = false): T[] => {
  const attrs = catalog.products.find((p) => p.id === master)?.variant_attributes ?? [];
  const rank = (p: T) =>
    attrs.map((a) => {
      const i = (a.values ?? []).findIndex((v) => v.name === p.variant_values?.[a.name]);
      return i < 0 ? 999 : i;
    });
  const order = (a: T, b: T) => {
    const ra = rank(a);
    const rb = rank(b);
    for (let i = 0; i < ra.length; i++) if (ra[i] !== rb[i]) return ra[i]! - rb[i]!;
    return a.name.localeCompare(b.name);
  };
  return catalog.products.filter((p) => p.variant_of === master && (all || p.on_menu !== false)).sort(order);
};

const label = (p: CatalogProduct) => Object.values(p.variant_values ?? {}).join(' · ') || p.name;

/** Asks which variant to sell: one large tile per size or colour, with its price now. */
export function VariantDialog({ catalog, master, at, onPick, onClose }: { catalog: Catalog; master: string; at: Date; onPick: (variant: string) => void; onClose: () => void }) {
  const product = catalog.products.find((p) => p.id === master);
  const variants = sellableVariants(catalog, master);
  return (
    <Dialog title={t('variants.choose', { name: product?.name ?? '' })} onClose={onClose}>
      {variants.length ? (
        <div className="variant-picker" role="list">
          {variants.map((v) => (
            <button
              key={v.id}
              role="listitem"
              className="variant-tile"
              disabled={!v.available}
              onClick={() => {
                onPick(v.id);
                onClose();
              }}
            >
              <span className="variant-tile-name">{label(v)}</span>
              <span className="variant-tile-price">{v.available ? money(priceNow(catalog, v, at).price) : t('product.sold_out')}</span>
            </button>
          ))}
        </div>
      ) : (
        <EmptyState icon="box" title={t('variants.none_on_sale')} />
      )}
    </Dialog>
  );
}
